import { useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Activity,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Heart,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import type { Site } from './shared'

interface UptimeTabProps {
  site: Site
}

interface HeartbeatData {
  domain: string
  status: 'up' | 'warning' | 'down'
  lastHeartbeatAt: string
  wpVersion: string | null
  phpVersion: string | null
  jezpressVersion: string | null
  createdAt: string
  ageMs: number
  thresholds: {
    UP: number
    WARNING: number
  }
}

const statusConfig = {
  up: {
    icon: CheckCircle2,
    color: 'text-green-500',
    bg: 'bg-green-500/10',
    label: 'Online',
    description: 'Site is responding normally',
  },
  warning: {
    icon: Clock,
    color: 'text-yellow-500',
    bg: 'bg-yellow-500/10',
    label: 'Stale',
    description: 'No heartbeat in 30+ minutes',
  },
  down: {
    icon: XCircle,
    color: 'text-red-500',
    bg: 'bg-red-500/10',
    label: 'Offline',
    description: 'No heartbeat in 60+ minutes',
  },
}

export function UptimeTab({ site }: UptimeTabProps) {
  const [data, setData] = useState<HeartbeatData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchHeartbeat = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(
        `/api/heartbeat/sites/${encodeURIComponent(site.domain)}`
      )
      if (!response.ok) {
        if (response.status === 404) {
          setData(null)
          setLoading(false)
          return
        }
        throw new Error(`HTTP ${response.status}`)
      }
      const result = (await response.json()) as HeartbeatData
      setData(result)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchHeartbeat()
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchHeartbeat, 60000)
    return () => clearInterval(interval)
  }, [site.domain])

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-lg border p-4 space-y-3">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-20 w-full" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border p-6 text-center">
        <AlertTriangle className="h-8 w-8 text-destructive mx-auto mb-2" />
        <p className="text-sm text-muted-foreground">
          Failed to load heartbeat data: {error}
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-4"
          onClick={fetchHeartbeat}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="rounded-lg border p-6 text-center">
        <Heart className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
        <p className="font-medium">No heartbeat data yet</p>
        <p className="text-sm text-muted-foreground mt-1">
          This site hasn't sent a heartbeat yet. Make sure JezPress v2.19.0+ is
          installed and the site is online.
        </p>
        <p className="text-xs text-muted-foreground mt-3">
          Heartbeats are sent every 15 minutes automatically.
        </p>
      </div>
    )
  }

  const config = statusConfig[data.status]
  const StatusIcon = config.icon

  return (
    <div className="space-y-6">
      {/* Current Status Card */}
      <div className="rounded-lg border p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-medium">
            <Heart className="h-4 w-4 text-muted-foreground" />
            Heartbeat Status
          </div>
          <Button variant="ghost" size="sm" onClick={fetchHeartbeat}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-full ${config.bg}`}>
            <StatusIcon className={`h-6 w-6 ${config.color}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-semibold">{config.label}</span>
              <Badge
                variant={
                  data.status === 'up'
                    ? 'default'
                    : data.status === 'warning'
                      ? 'secondary'
                      : 'destructive'
                }
              >
                {data.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{config.description}</p>
          </div>
        </div>

        {/* Last Seen */}
        <div className="pt-2 border-t">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Last seen</span>
            <span className="text-sm font-medium">
              {formatDistanceToNow(new Date(data.lastHeartbeatAt), {
                addSuffix: true,
              })}
            </span>
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {new Date(data.lastHeartbeatAt).toLocaleString()}
          </div>
        </div>
      </div>

      {/* Version Info Card */}
      {(data.wpVersion || data.phpVersion || data.jezpressVersion) && (
        <div className="rounded-lg border p-4 space-y-3">
          <div className="flex items-center gap-2 font-medium">
            <Activity className="h-4 w-4 text-muted-foreground" />
            Version Information
          </div>

          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground block">WordPress</span>
              <span className="font-mono">{data.wpVersion || '-'}</span>
            </div>
            <div>
              <span className="text-muted-foreground block">PHP</span>
              <span className="font-mono">{data.phpVersion || '-'}</span>
            </div>
            <div>
              <span className="text-muted-foreground block">JezPress</span>
              <span className="font-mono">{data.jezpressVersion || '-'}</span>
            </div>
          </div>
        </div>
      )}

      {/* How It Works Card */}
      <div className="rounded-lg border border-dashed p-4 space-y-2 bg-muted/30">
        <p className="text-sm font-medium">How heartbeats work</p>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>
            • Sites send heartbeats every 15 minutes to indicate they're online
          </li>
          <li>
            • <span className="text-green-600 font-medium">Online</span>: Last
            heartbeat within 30 minutes
          </li>
          <li>
            • <span className="text-yellow-600 font-medium">Stale</span>: Last
            heartbeat 30-60 minutes ago
          </li>
          <li>
            • <span className="text-red-600 font-medium">Offline</span>: No
            heartbeat for over 60 minutes
          </li>
        </ul>
      </div>
    </div>
  )
}
